import { AlertMessage } from '@/app/components'
import { useCurrentMultiProgramFlowContext } from '@/app/context/MultiProgram/MultiProgramFlowContexts'
import { useNavigation } from '@/app/hooks'
import useEnrollableDevices from '@/app/hooks/useEnrollableDevices'
import useUtilityPrograms from '@/app/hooks/useUtilityPrograms'
import { parseTagline } from '@/app/pages/multi-program-enrollment/utils/utils'
import { Button, Text } from '@/components'
import { UtilityProgram } from '@/types/utilityProgram'
import getProgramEnrollmentExpiration from '@/utils/utility-program-helpers/getProgramEnrollmentExpiration'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { match } from 'ts-pattern'
import { AdditionalEnrollmentModal } from './AdditionalEnrollmentModal'
import { DeviceUrlSlug } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.types'
import VerificationStatusTag from './VerificationStatusTag/VerificationStatusTag'
import { deviceTypeFromUrlSlug } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.utils'
import {
  useDeviceVerificationStatus,
  VerificationStatusTagType,
} from '@/hooks/useDeviceVerificationStatus/useDeviceVerificationStatus'
import { useAppSelector } from '@/hooks'
import { selectEnrolledPrograms } from '@/reducers/programsNew'
import { useHasExistingApplication } from '@/reducers/utilityProgramApplications'

type Props = {
  program: UtilityProgram
  enrolled?: boolean
  bannerClassName?: string
}
export default function ProgramCard({
  program,
  enrolled,
  bannerClassName,
}: Props) {
  const { enrollableDeviceCount } = useEnrollableDevices(program.id)
  const [modalOpen, setModalOpen] = useState(false)
  const enrolledPrograms = useAppSelector(selectEnrolledPrograms(true))
  const navigate = useNavigation()
  const { deviceId: deviceIdParam } = useParams<{ deviceId?: string }>()
  const { deviceType } = useParams<{ deviceType?: DeviceUrlSlug }>()
  // Enrolled can be enforced as a prop, otherwise check to see if this program is enrolled by any devices
  enrolled = enrolled ?? enrolledPrograms.some((p) => p.id === program.id)

  const hasExistingApplication = useHasExistingApplication(program.id)

  const { deviceStatus, profileStatus } = useDeviceVerificationStatus({
    programId: program.id,
    deviceType: deviceType ? deviceTypeFromUrlSlug(deviceType) : undefined,
    deviceId: deviceIdParam,
  })

  const { formattedExpirationDate, isExpired } = getProgramEnrollmentExpiration(
    program.preferred_display_timezone,
    program.view_config_json?.programCard?.enrollmentEndDate,
    program.view_config_json?.programCard?.rollingDeadline ?? false,
  )

  function handleOpenProgramRequirements() {
    if (selectedTeslaIsMissingPermissions && deviceIdParam) {
      navigate.push(`/devices/vehicle/${deviceIdParam}/capability`)
      return
    }
    onProgramCardClicked(
      program,
      deviceIsEligibleAndUnenrolled ? true : enrolled,
    )
  }

  const sponsorImg = program?.view_config_json?.logoUrl ?? null

  const taglineParts = parseTagline(
    program.view_config_json.programCard?.savingsTagline ?? '',
  )

  const { onProgramCardClicked } = useCurrentMultiProgramFlowContext()

  const {
    enrolledProgramsSelectedDeviceCanBeAddedTo,
    selectedTeslaIsMissingPermissions,
  } = useUtilityPrograms({ onlyMultiPrograms: true })

  const deviceIsEligibleAndUnenrolled =
    enrolledProgramsSelectedDeviceCanBeAddedTo.find(
      (p) => p.id === program.id,
    ) !== undefined

  const additionalDevicesAvailable = enrollableDeviceCount > 0
  // Defines if this device is already enrolled in the program, or it COULD be
  const enrolledOrEnrollable = enrolled || deviceIsEligibleAndUnenrolled
  const showEnrollAdditional =
    additionalDevicesAvailable &&
    enrolledOrEnrollable &&
    !isExpired &&
    ![
      VerificationStatusTagType.INELIGIBLE,
      VerificationStatusTagType.COMPLETED,
      VerificationStatusTagType.INACTIVE,
    ].includes(deviceStatus)

  const dateColor = isExpired ? 'text-base-orange-500' : 'text-base-green-900'

  const showCTA =
    (deviceStatus !== VerificationStatusTagType.INELIGIBLE &&
      deviceStatus !== VerificationStatusTagType.INACTIVE) ||
    profileStatus === 'VERIFIED'

  const buttonCta = match({
    enrolled,
    showReEnrollDisclaimer: deviceIsEligibleAndUnenrolled,
    selectedTeslaIsMissingPermissions,
    deviceStatus,
    profileStatus,
    active: program.active,
  })
    .when(
      ({ selectedTeslaIsMissingPermissions }) =>
        selectedTeslaIsMissingPermissions,
      () => 'Fix Permissions',
    )
    .when(
      ({ enrolled, showReEnrollDisclaimer, deviceStatus, active }) =>
        enrolled ||
        showReEnrollDisclaimer ||
        deviceStatus === VerificationStatusTagType.INELIGIBLE ||
        !active,
      () => 'Manage',
    )
    .otherwise(() =>
      hasExistingApplication || program.verification_status === 'ABANDONED'
        ? 'Continue Applying'
        : 'Apply Now',
    )

  return (
    <section
      className={`flex flex-col w-full ${!enrolled ? 'cursor-pointer' : ''}`}
    >
      <div
        className={twMerge(
          `bg-base-green-100 [&>*]:text-base-green-900 flex flex-row gap-1 items-center px-4 py-2 rounded-t-2xl`,
          bannerClassName,
        )}
      >
        <Text className={'font-semibold'}>{taglineParts?.[0]}</Text>
        <Text variant={'h3'}>{taglineParts?.[1]}</Text>
        <Text className={'font-semibold'}>{taglineParts?.[2]}</Text>
      </div>
      <div className={'bg-base-white p-4 rounded-b-2xl flex flex-col gap-2'}>
        <div className="flex justify-between gap-4 align-self-stretch">
          <Text variant="h3">
            {program.view_config_json?.name ?? program.name}
          </Text>
          {deviceIdParam && deviceType && (
            <VerificationStatusTag
              programId={program.id}
              deviceId={deviceIdParam}
              deviceType={deviceTypeFromUrlSlug(deviceType)}
            />
          )}
        </div>

        {/* Evenly spaced grid-like layout */}
        <div className="grid grid-cols-2">
          {/* Sponsor icon w/ vertical divider */}
          {sponsorImg && (
            <div className="flex pl-2 relative">
              <div className="flex flex-col gap-0.5">
                <Text variant={'dimmed'}>Sponsor</Text>
                <img
                  src={sponsorImg}
                  alt="Program Sponsor"
                  className="max-w-[80px] h-auto object-fit-contain"
                />
              </div>
            </div>
          )}
          {/* Deadline section */}
          {(deviceStatus === VerificationStatusTagType.AVAILABLE ||
            deviceStatus === VerificationStatusTagType.ELIGIBLE) && (
            <div
              className={`flex flex-col gap-0.5 border-l-2 relative ${
                sponsorImg ? 'pl-6' : ''
              }`}
            >
              {(isExpired || formattedExpirationDate) && (
                <div>
                  {sponsorImg && (
                    <div className="absolute left-0 w-[1px] bg-base-grey-200 h-10"></div>
                  )}
                  <Text variant={'dimmed'}>
                    {isExpired ? 'Expired' : 'Deadline'}
                  </Text>
                  <Text className={`${dateColor}`}>
                    {`${formattedExpirationDate}`}
                  </Text>
                </div>
              )}
            </div>
          )}
        </div>
        {showCTA && (
          <div className="mt-4 flex flex-col gap-2" id="btn-container">
            {selectedTeslaIsMissingPermissions && (
              <AlertMessage variant="info">
                In order to enroll this Tesla, you'll need to fix its
                permissions.
              </AlertMessage>
            )}
            <Button
              id="nav-btn"
              small
              onClick={handleOpenProgramRequirements}
              variant={showEnrollAdditional ? 'primary-subtle' : 'primary'}
            >
              {buttonCta}
            </Button>
            {showEnrollAdditional && (
              <Button
                id="additional-device-btn"
                small
                onClick={() => setModalOpen(true)}
              >
                Enroll Additional Device(s)
              </Button>
            )}
          </div>
        )}
      </div>
      <AdditionalEnrollmentModal
        programId={program.id}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </section>
  )
}
